/**
 * useOneSignalPush — the OneSignal opt-in adapter.
 *
 * Presents the same surface as the legacy VAPID hook (a Pick of
 * UseWebPushNotificationsReturn) so OneSignalPushInitializer can reuse the
 * existing useWebPushPermissionPrompt + WebPushPermissionDrawer choreography
 * verbatim. Only mounted when OneSignal owns push (isOneSignalPushOwner()); the
 * legacy hook stays the sole path otherwise.
 *
 * Design:
 *   - isSupported = OneSignal configured AND the browser supports web push.
 *   - permission comes from the shared useNotificationPermission store (same one
 *     the legacy hook reads), so a granted/denied change reflects everywhere.
 *   - isSubscribed mirrors the SDK's PushSubscription.optedIn, synced through the
 *     SDK 'change' event in ONE effect (the sanctioned external-system-sync use
 *     of useEffect). Seeded from the current optedIn snapshot once ready.
 *   - Every SDK touch is gated behind whenOneSignalReady().then(...).catch(log):
 *     W-A's readiness promise REJECTS when init failed / never ran, so callers
 *     must catch. subscribe()/unsubscribe() therefore always resolve (a broken
 *     OneSignal never rejects into the prompt choreography).
 *
 * SDK method shapes verified against react-onesignal 3.5.6
 * (node_modules/react-onesignal/dist/index.d.ts):
 *   - Notifications.requestPermission(): Promise<boolean>
 *   - User.PushSubscription.optedIn: boolean | undefined; optIn()/optOut(): Promise<void>
 *   - PushSubscription.addEventListener('change', cb) → cb({ current: { optedIn } })
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import OneSignal from 'react-onesignal';
import { isOneSignalConfigured, whenOneSignalReady } from '@lib/onesignal';
import {
  useNotificationPermission,
  notifyPermissionChange,
} from '@hooks/useNotificationPermission';
import { toast } from '@hooks/use-toast';
import { isWebPushSupported } from '@utils/webPush';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import type { UseWebPushNotificationsReturn } from '@hooks/useWebPushNotifications';

/**
 * The subset of the legacy hook's surface the permission prompt + drawer consume.
 * Deliberately omits `isVapidRotationFailed` (a legacy-only concern) so a
 * consumer written against this type can't accidentally reach for it.
 */
export type OneSignalPushInterface = Pick<
  UseWebPushNotificationsReturn,
  | 'isSupported'
  | 'permission'
  | 'isSubscribed'
  | 'isLoading'
  | 'isSubscribing'
  | 'isUnsubscribing'
  | 'subscribe'
  | 'unsubscribe'
>;

export const useOneSignalPush = (): OneSignalPushInterface => {
  // Pure env + capability gate. isOneSignalConfigured() already excludes native;
  // isWebPushSupported() covers the browser feature set. Both are stable for the
  // life of the tab, so a single mount-time memo is enough.
  const isSupported = useMemo(() => isOneSignalConfigured() && isWebPushSupported(), []);

  // Shared reactive Notification.permission (same store the legacy hook reads).
  const permission = useNotificationPermission();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);

  // Sync isSubscribed from the SDK subscription 'change' event. This is external
  // system synchronisation (the OneSignal SDK), the one sanctioned useEffect use.
  // Seed from the current optedIn snapshot once ready, then track live changes.
  // Guarded by isSupported so the dormant path never touches the SDK.
  useEffect(() => {
    if (!isSupported) return;

    let cancelled = false;
    // Typed loosely because the SDK's SubscriptionChangeEvent only surfaces
    // `current.optedIn` (boolean); we don't need the rest of the payload.
    const handleChange = (change: { current: { optedIn: boolean } }) => {
      if (!cancelled) setIsSubscribed(change.current.optedIn);
    };

    whenOneSignalReady()
      .then(() => {
        if (cancelled) return;
        // Seed the initial value from the SDK snapshot.
        setIsSubscribed(Boolean(OneSignal.User.PushSubscription.optedIn));
        OneSignal.User.PushSubscription.addEventListener('change', handleChange);
      })
      .catch((error: unknown) => {
        // Readiness rejects when OneSignal is unconfigured / init failed. Nothing
        // to sync; leave isSubscribed at its default false.
        logger.error(
          'OneSignal push: readiness failed while syncing subscription',
          sanitizeError(error),
        );
      });

    return () => {
      cancelled = true;
      // Safe to call even if the listener was never added (readiness rejected or
      // was still pending): removeEventListener on an unknown handler is a no-op.
      OneSignal.User.PushSubscription.removeEventListener('change', handleChange);
    };
  }, [isSupported]);

  const subscribe = useCallback(async (): Promise<void> => {
    if (!isSupported) return;
    setIsSubscribing(true);
    try {
      await whenOneSignalReady();
      // Native browser permission request. requestPermission() resolves to a
      // boolean; poke the shared store so `permission` reflects the new value
      // immediately (parity with the legacy hook's post-request notify).
      const granted = await OneSignal.Notifications.requestPermission();
      notifyPermissionChange();
      if (!granted) {
        logger.info('OneSignal push: permission not granted');
        return;
      }
      // optIn is idempotent but avoid a redundant call when already subscribed.
      if (!OneSignal.User.PushSubscription.optedIn) {
        await OneSignal.User.PushSubscription.optIn();
      }
    } catch (error) {
      // Readiness rejection or an SDK error: log and swallow so the prompt
      // choreography (which awaits this) never sees a rejection. Surface the same
      // error toast the legacy hook shows on this drawer for parity (a permission
      // decline is handled above with an early return, so it stays toast-free).
      logger.error('OneSignal push: subscribe failed', sanitizeError(error));
      toast({
        title: 'Failed to enable notifications',
        description: 'Please try again.',
        variant: 'error',
      });
    } finally {
      setIsSubscribing(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!isSupported) return;
    setIsUnsubscribing(true);
    try {
      await whenOneSignalReady();
      await OneSignal.User.PushSubscription.optOut();
    } catch (error) {
      // Log + swallow (the prompt choreography awaits this), and surface the same
      // error toast the legacy hook shows on this drawer for parity.
      logger.error('OneSignal push: unsubscribe failed', sanitizeError(error));
      toast({
        title: 'Failed to disable notifications',
        description: 'Please try again.',
        variant: 'error',
      });
    } finally {
      setIsUnsubscribing(false);
    }
  }, [isSupported]);

  return {
    isSupported,
    permission,
    isSubscribed,
    // OneSignal has no equivalent of the legacy hook's subscription/VAPID-key
    // queries; readiness is fire-and-forget, so there's no distinct loading gate.
    isLoading: false,
    isSubscribing,
    isUnsubscribing,
    subscribe,
    unsubscribe,
  };
};
